import { useState } from "react";
import FileDropZone from "./FileDropZone";
import va from "@vercel/analytics";
import VetRecButton from "../common/VetRecButton";

interface FileUploaderProps {
  sessionId: string | undefined;
  onSubmitButtonClick: (files: File[]) => Promise<void>;
  submitButtonText?: string;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  sessionId,
  onSubmitButtonClick,
  submitButtonText,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string | undefined>(undefined);

  const getLoggingProperties = () => {
    return {
      date: new Date().toUTCString(),
      sessionId: sessionId ?? "undefined",
    };
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileUploadErrorMessage(undefined);
    const uploadedFiles = event.target.files;
    if (uploadedFiles) {
      const filesArray = Array.from(uploadedFiles);
      const combinedFiles = [...files, ...filesArray];
      const deduplicatedFiles = combinedFiles.reduce<File[]>((acc, currentFile) => {
        if (!acc.some(file => file.name === currentFile.name)) {
          acc.push(currentFile);
        }
        return acc;
      }, []);
      setFiles(deduplicatedFiles);
    }

    va.track("Audio_Upload", getLoggingProperties());
  };

  async function handleSubmit() {
    if (files.length === 0) {
      setFileUploadErrorMessage("Please add files");
      return;
    }

    const empty_file = files.find(f => f.size === 0)

    if (empty_file) {
      setFileUploadErrorMessage(`The following file is empty: ${empty_file.name}. Please remove it and try again.`)
      return
    }

    await onSubmitButtonClick(files);

    setFiles([]);
    va.track("Audio_Submit", getLoggingProperties());
  }

  return (
    <div className="">
      <FileDropZone
        files={files}
        onFileUpload={handleFileUpload}
        invalidFilesUploaded={fileUploadErrorMessage !== undefined}
        subText="Supported file types include .m4a, .mp4, .mp3, .wav, .webm"
        accept="audio/*,video/mp4"
      />
      {fileUploadErrorMessage && (
        <span className="text-xs text-red-600">
          {fileUploadErrorMessage}
        </span>
      )}
      <VetRecButton
        type="button"
        className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 mt-4"
        onClick={() => {handleSubmit().catch(() => {})}}
      >
        {submitButtonText ?? "Summarize"}
      </VetRecButton>
    </div>
  );
};
